/**
 * NetworkCanvas v2 — Visual cabling canvas for network infrastructure.
 *
 * Left / Right columns each support any combination of:
 *   • Switches     → switch_port
 *   • Patch Panels → panel_port
 *   • Servers NIC  → component_port (nic)
 *
 * Views:
 *   ⊞ Cuadrícula  – port squares with SVG bezier cables (default)
 *   ≡ Lista        – card list with port rows
 *   ⤢ Diagrama    – compact SVG wiring overview
 *
 * Interaction:
 *   1. Left port  → click free port → selected (blue glow)
 *   2. Right port → click free port → ConnectForm modal → Save
 *   3. Any conn.  → click connected port → DisconnectConfirm
 *   4. Escape     → cancel selection
 */

import React, {
  useState, useEffect, useCallback, useRef,
  useLayoutEffect, useMemo,
} from 'react';
import api from '../services/api';

// ── Constants ─────────────────────────────────────────────────────────────────

const CABLE_TYPES = [
  'Cat5e', 'Cat6', 'Cat6a', 'Cat7', 'Cat8',
  'Fibra MM (OM3/OM4)', 'Fibra SM (OS2)',
  'DAC (cobre directo)', 'AOC (fibra activa)', 'Otro',
];

const CABLE_COLORS = [
  { value: 'negro',    label: 'Negro',    hex: '#1f2937' },
  { value: 'gris',     label: 'Gris',     hex: '#9ca3af' },
  { value: 'blanco',   label: 'Blanco',   hex: '#e5e7eb' },
  { value: 'rojo',     label: 'Rojo',     hex: '#ef4444' },
  { value: 'azul',     label: 'Azul',     hex: '#3b82f6' },
  { value: 'verde',    label: 'Verde',    hex: '#22c55e' },
  { value: 'amarillo', label: 'Amarillo', hex: '#eab308' },
  { value: 'naranja',  label: 'Naranja',  hex: '#f97316' },
  { value: 'violeta',  label: 'Violeta',  hex: '#8b5cf6' },
  { value: 'celeste',  label: 'Celeste',  hex: '#38bdf8' },
];

const DEVICE_TYPES = {
  switch: {
    key: 'switch', label: 'Switches', icon: '🔀',
    portType: 'switch_port', color: '#3b82f6',
    async load(cid)              { return (await api.getElements(cid, { element_type: 'switch' })).data || []; },
    async loadPorts(id, cid)     { return (await api.getPorts(id, cid)).data || []; },
  },
  panel: {
    key: 'panel', label: 'Patch Panels', icon: '🔗',
    portType: 'panel_port', color: '#8b5cf6',
    async load(cid)              { return (await api.getElements(cid, { element_type: 'panel' })).data || []; },
    async loadPorts(id, cid)     { return (await api.getPorts(id, cid)).data || []; },
  },
  server_nic: {
    key: 'server_nic', label: 'Servidores (NIC)', icon: '🖥',
    portType: 'component_port', color: '#10b981',
    async load(cid)              { return (await api.getServers(cid)).data || []; },
    async loadPorts(id, cid)     { return ((await api.getPorts(id, cid)).data || []).filter(p => p.component_type === 'nic'); },
  },
  server_bmc: {
    key: 'server_bmc', label: 'iLO / BMC', icon: '🔧',
    portType: 'component_port', color: '#f97316',
    async load(cid)              { return (await api.getServers(cid)).data || []; },
    async loadPorts(id, cid)     { return ((await api.getPorts(id, cid)).data || []).filter(p => p.component_type === 'bmc'); },
  },
  storage: {
    key: 'storage', label: 'Almacenamiento', icon: '🗄️',
    portType: 'component_port', color: '#14b8a6',
    async load(cid)              { return (await api.getStorageArrays(cid)).data || []; },
    async loadPorts(id, cid)     { return ((await api.getPorts(id, cid)).data || []).filter(p => p.component_type === 'nic' || p.component_type === 'hba'); },
  },
  ip_phone: {
    key: 'ip_phone', label: 'Teléfonos IP', icon: '☎️',
    portType: 'ip_phone_port', color: '#0ea5e9',
    async load(cid)              { return (await api.getIPPhones(cid)).data || []; },
    async loadPorts(id, cid)     { return (await api.getIPPhone(id, cid)).data?.ports || []; },
  },
  printer: {
    key: 'printer', label: 'Impresoras', icon: '🖨️',
    portType: 'printer_port', color: '#f59e0b',
    async load(cid)              { return (await api.getPrinters({ company_id: cid })).data || []; },
    async loadPorts(id, cid)     { return (await api.getPrinterPorts(id, cid)).data || []; },
  },
  workstation: {
    key: 'workstation', label: 'Workstations', icon: '💻',
    portType: 'workstation_port', color: '#8b5cf6',
    async load(cid)              { return (await api.getWorkstations({ company_id: cid })).data || []; },
    async loadPorts(id, cid)     { return (await api.getWorkstationPorts(id, cid)).data || []; },
  },
  router: {
    key: 'router', label: 'Routers', icon: '📡',
    portType: 'router_iface', color: '#ef4444',
    async load(cid)              { return (await api.getRouters(cid)).data || []; },
    async loadPorts(id, cid)     { return (await api.getRouterPorts(id, cid)).data || []; },
  },
  firewall: {
    key: 'firewall', label: 'Firewalls', icon: '🛡️',
    portType: 'firewall_iface', color: '#dc2626',
    async load(cid)              { return (await api.getFirewalls(cid)).data || []; },
    async loadPorts(id, cid)     { return (await api.getFirewallPorts(id, cid)).data || []; },
  },
};

function colorHex(name) {
  return CABLE_COLORS.find(c => c.value === name)?.hex ?? '#9ca3af';
}

// Inject peer-port pulse keyframe once
if (typeof document !== 'undefined' && !document.getElementById('nc-kf')) {
  const s = document.createElement('style');
  s.id = 'nc-kf';
  s.textContent = '@keyframes nc-pulse { 0%,100% { box-shadow: 0 0 0 2px var(--port-selected-bd), 0 0 0 7px rgba(249,115,22,0.22); } 50% { box-shadow: 0 0 0 3px var(--port-selected-bd), 0 0 0 12px rgba(249,115,22,0.10); } }';
  document.head.appendChild(s);
}

// ── Device-type selector (pills) ─────────────────────────────────────────────

function TypeSelector({ side, selected, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 70 }}>
        {side === 'left' ? '◀ Origen' : 'Destino ▶'}
      </span>
      {Object.values(DEVICE_TYPES).map(dt => {
        const active = selected.includes(dt.key);
        return (
          <button key={dt.key}
            onClick={() => {
              if (active && selected.length === 1) return;
              onChange(active ? selected.filter(k => k !== dt.key) : [...selected, dt.key]);
            }}
            title={active ? `Quitar ${dt.label}` : `Añadir ${dt.label}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', borderRadius: 20, cursor: 'pointer', fontSize: '0.78rem',
              border: `2px solid ${active ? dt.color : 'var(--slate-300)'}`,
              background: active ? `${dt.color}18` : 'var(--bg-input)',
              color: active ? dt.color : 'var(--text-secondary)',
              fontWeight: active ? 700 : 400,
              transition: 'all 0.12s',
            }}
          >
            {dt.icon} {dt.label}
            {active && <span style={{ fontSize: '0.6rem', background: dt.color, color: '#fff', borderRadius: '50%', width: 14, height: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>✓</span>}
          </button>
        );
      })}
    </div>
  );
}

// ── Device multi-select (collapsible dropdown) ────────────────────────────

function DeviceFilterControl({ label, options, selectedIds, onChange }) {
  const [open, setOpen]       = useState(false);
  const [search, setSearch]   = useState('');
  const ref                   = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const filtered = search.trim()
    ? options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()))
    : options;

  const noneSelected = selectedIds.length === 0;
  const btnLabel     = noneSelected
    ? `Todos (${options.length})`
    : `${selectedIds.length} seleccionado${selectedIds.length !== 1 ? 's' : ''}`;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => { setSearch(''); setOpen(o => !o); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '5px 12px', borderRadius: 8, cursor: 'pointer',
          border: noneSelected ? '1px solid var(--slate-300)' : '1.5px solid var(--accent)',
          background: noneSelected ? 'var(--bg-input)' : 'rgba(var(--accent-rgb),.18)',
          color: noneSelected ? 'var(--text-secondary)' : 'var(--accent-l)',
          fontSize: '0.8rem', fontWeight: noneSelected ? 400 : 600,
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ fontWeight: 700 }}>{label}:</span> {btnLabel} <span style={{ fontSize: '0.65rem' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
          background: 'var(--bg-surface)', border: '1px solid var(--slate-300)', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)', minWidth: 230, maxWidth: 320, padding: 8,
        }}>
          <input
            type="text"
            placeholder="Buscar dispositivo..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '5px 8px', border: '1px solid var(--slate-300)', borderRadius: 6,
              fontSize: '0.8rem', marginBottom: 6, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)',
            }}
          />
          <button
            onClick={() => onChange([])}
            style={{
              width: '100%', padding: '4px 0', marginBottom: 4,
              border: '1px solid var(--slate-300)', borderRadius: 6, background: 'var(--bg-input)',
              color: 'var(--text-primary)', fontSize: '0.75rem', cursor: 'pointer',
            }}
          >
            Limpiar filtro (mostrar todos)
          </button>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filtered.length === 0 && (
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', padding: '4px 6px' }}>Sin resultados</div>
            )}
            {filtered.map(opt => {
              const checked = selectedIds.includes(opt.id);
              return (
                <label key={opt.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '4px 6px', borderRadius: 6, cursor: 'pointer',
                  background: checked ? 'rgba(var(--accent-rgb),.1)' : 'transparent',
                }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      if (checked) onChange(selectedIds.filter(x => x !== opt.id));
                      else onChange([...selectedIds, opt.id]);
                    }}
                    style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-primary)', userSelect: 'none' }}>{opt.name}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Modals ────────────────────────────────────────────────────────────────────

function ConnectForm({ sourcePort, sourceMeta, destPort, destMeta, companyId, onSave, onCancel }) {
  const [form, setForm]     = useState({ cable_type: 'Cat6', cable_color: 'azul', cable_length_m: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      await api.createConnection({
        port_a_type:    sourceMeta.portType,
        port_a_id:      sourcePort.id,
        port_b_type:    destMeta.portType,
        port_b_id:      destPort.id,
        cable_type:     form.cable_type     || null,
        cable_color:    form.cable_color    || null,
        cable_length_m: form.cable_length_m ? parseFloat(form.cable_length_m) : null,
        notes:          form.notes          || null,
      }, companyId);
      onSave();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  const srcDT = DEVICE_TYPES[sourceMeta.typeKey];
  const dstDT = DEVICE_TYPES[destMeta.typeKey];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--modal-overlay)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
<div style={{ background: 'var(--bg-surface)', borderRadius: 14, padding: '1.75rem', width: 430, boxShadow: 'var(--shadow-lg)', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1.05rem', color: 'var(--text-primary)' }}>🔌 Conectar cable de red</h3>

        <div style={{ background: 'var(--bg-toolbar)', borderRadius: 8, padding: '0.65rem 0.9rem', marginBottom: '1.25rem', fontSize: '0.82rem', color: 'var(--text-primary)', lineHeight: 1.8 }}>
          <div><span style={{ color: 'var(--text-muted)' }}>Desde {srcDT?.icon} {srcDT?.label}:</span>{' '}
            <strong>{sourceMeta.deviceName}</strong> · {sourcePort.label || `P${sourcePort.port_number}`}
            {sourcePort.speed ? ` (${sourcePort.speed})` : ''}
          </div>
          <div><span style={{ color: 'var(--text-muted)' }}>Hacia {dstDT?.icon} {dstDT?.label}:</span>{' '}
            <strong>{destMeta.deviceName}</strong> · {destPort.label || `P${destPort.port_number}`}
            {destPort.speed ? ` (${destPort.speed})` : ''}
            {destPort.mac_address ? <span style={{ color: 'var(--text-muted)' }}> · {destPort.mac_address}</span> : ''}
          </div>
        </div>

        {error && <div style={{ color: '#fca5a5', fontSize: '0.8rem', marginBottom: '0.75rem', background: 'rgba(239,68,68,.15)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>{error}</div>}

        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>Tipo de cable</label>
        <select value={form.cable_type} onChange={e => setForm({ ...form, cable_type: e.target.value })}
          style={{ width: '100%', marginBottom: '1rem', padding: '0.45rem 0.6rem', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.85rem' }}>
          {CABLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 8 }}>Color del cable</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: '1rem', flexWrap: 'wrap' }}>
          {CABLE_COLORS.map(c => (
            <button key={c.value} onClick={() => setForm({ ...form, cable_color: c.value })} title={c.label}
              style={{ width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', background: c.hex,
                border: form.cable_color === c.value ? '3px solid var(--accent)' : c.value === 'blanco' ? '2px solid var(--border)' : '2px solid transparent',
                boxShadow: form.cable_color === c.value ? '0 0 0 2px rgba(var(--accent-rgb),.35)' : 'none' }} />
          ))}
        </div>

        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
          Longitud (m) <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(opcional)</span>
        </label>
        <input type="number" min="0" step="0.5" value={form.cable_length_m} onChange={e => setForm({ ...form, cable_length_m: e.target.value })}
          placeholder="Ej: 1.5"
          style={{ width: '100%', marginBottom: '1rem', padding: '0.45rem 0.6rem', borderRadius: 6, border: '1px solid var(--border)', fontSize: '0.85rem', boxSizing: 'border-box' }} />

        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
          Notas <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(opcional)</span>
        </label>
        <input type="text" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
          placeholder="Ej: Uplink server principal - rack A"
          onKeyDown={e => e.key === 'Enter' && save()}
          style={{ width: '100%', marginBottom: '1.25rem', padding: '0.45rem 0.6rem', borderRadius: 6, border: '1px solid var(--border)', fontSize: '0.85rem', boxSizing: 'border-box' }} />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} disabled={saving}
            style={{ padding: '0.5rem 1.1rem', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: '0.85rem' }}>Cancelar</button>
          <button onClick={save} disabled={saving}
            style={{ padding: '0.5rem 1.3rem', borderRadius: 7, border: 'none', background: saving ? 'var(--accent-l)' : 'var(--btn-primary)', color: '#fff', fontWeight: 700, cursor: saving ? 'wait' : 'pointer', fontSize: '0.85rem' }}>
            {saving ? 'Guardando…' : '🔌 Conectar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConnectionActionModal({ conn, companyId, onDone, onCancel }) {
  const [form, setForm] = useState({
    cable_type:     conn.cable_type     || 'Cat6',
    cable_color:    conn.cable_color    || 'azul',
    cable_length_m: conn.cable_length_m != null ? String(conn.cable_length_m) : '',
    notes:          conn.notes          || '',
  });
  const [saving,    setSaving]    = useState(false);
  const [removing,  setRemoving]  = useState(false);
  const [error,     setError]     = useState(null);
  const [confirmDc, setConfirmDc] = useState(false);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      await api.updateConnection(conn.id, {
        cable_type:     form.cable_type     || null,
        cable_color:    form.cable_color    || null,
        cable_length_m: form.cable_length_m ? parseFloat(form.cable_length_m) : null,
        notes:          form.notes          || null,
      }, companyId);
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  const disconnect = async () => {
    setRemoving(true); setError(null);
    try { await api.deleteConnection(conn.id, companyId); onDone(); }
    catch (e) { setError(e.message); setRemoving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--modal-overlay)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 14, padding: '1.75rem', width: 430, boxShadow: 'var(--shadow-lg)', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1.05rem', color: 'var(--text-primary)' }}>🔌 Editar conexión</h3>

        <div style={{ background: 'var(--bg-toolbar)', borderRadius: 8, padding: '0.65rem 0.9rem', marginBottom: '1.25rem', fontSize: '0.82rem', color: 'var(--text-primary)', lineHeight: 1.8 }}>
          <div><span style={{ color: 'var(--text-muted)' }}>Entre:</span>{' '}<strong>{conn.deviceA}</strong> · {conn.portA}</div>
          <div><span style={{ color: 'var(--text-muted)' }}>Y:</span>{' '}<strong>{conn.deviceB}</strong> · {conn.portB}</div>
        </div>

        {error && <div style={{ color: 'var(--error-text)', fontSize: '0.8rem', marginBottom: '0.75rem', background: 'var(--error-bg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>{error}</div>}

        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Tipo de cable</label>
        <select value={form.cable_type} onChange={e => setForm({ ...form, cable_type: e.target.value })}
          style={{ width: '100%', marginBottom: '1rem', padding: '0.45rem 0.6rem', borderRadius: 6, border: '1px solid var(--border)', fontSize: '0.85rem' }}>
          {CABLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>Color del cable</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: '1rem', flexWrap: 'wrap' }}>
          {CABLE_COLORS.map(c => (
            <button key={c.value} onClick={() => setForm({ ...form, cable_color: c.value })} title={c.label}
              style={{ width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', background: c.hex,
                border: form.cable_color === c.value ? '3px solid var(--accent)' : c.value === 'blanco' ? '2px solid var(--border)' : '2px solid transparent',
                boxShadow: form.cable_color === c.value ? '0 0 0 2px rgba(var(--accent-rgb),.35)' : 'none' }} />
          ))}
        </div>

        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
          Longitud (m) <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(opcional)</span>
        </label>
        <input type="number" min="0" step="0.5" value={form.cable_length_m}
          onChange={e => setForm({ ...form, cable_length_m: e.target.value })} placeholder="Ej: 1.5"
          style={{ width: '100%', marginBottom: '1rem', padding: '0.45rem 0.6rem', borderRadius: 6, border: '1px solid var(--border)', fontSize: '0.85rem', boxSizing: 'border-box' }} />

        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
          Notas <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(opcional)</span>
        </label>
        <input type="text" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
          placeholder="Ej: Uplink server principal - rack A"
          onKeyDown={e => e.key === 'Enter' && save()}
          style={{ width: '100%', marginBottom: '1.25rem', padding: '0.45rem 0.6rem', borderRadius: 6, border: '1px solid var(--border)', fontSize: '0.85rem', boxSizing: 'border-box' }} />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
          {!confirmDc
            ? <button onClick={() => setConfirmDc(true)} disabled={saving || removing}
                style={{ marginRight: 'auto', padding: '0.45rem 1rem', borderRadius: 7, border: '1px solid var(--error-bd)', background: 'var(--error-bg)', color: 'var(--btn-danger-hover)', fontSize: '0.8rem', cursor: 'pointer' }}>
                ✂️ Desconectar
              </button>
            : <span style={{ marginRight: 'auto', display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.8rem', color: 'var(--error-text)' }}>
                <span>¿Seguro?</span>
                <button onClick={disconnect} disabled={removing}
                  style={{ padding: '0.35rem 0.75rem', borderRadius: 6, border: 'none', background: 'var(--btn-danger)', color: '#fff', fontWeight: 700, cursor: removing ? 'wait' : 'pointer', fontSize: '0.8rem' }}>
                  {removing ? 'Cortando…' : 'Sí, cortar'}
                </button>
                <button onClick={() => setConfirmDc(false)}
                  style={{ padding: '0.35rem 0.75rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: '0.8rem' }}>No</button>
              </span>
          }
          <button onClick={onCancel} disabled={saving || removing}
            style={{ padding: '0.5rem 1.1rem', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: '0.85rem' }}>Cancelar</button>
          <button onClick={save} disabled={saving || removing}
            style={{ padding: '0.5rem 1.3rem', borderRadius: 7, border: 'none', background: saving ? 'var(--accent-l)' : 'var(--btn-primary)', color: '#fff', fontWeight: 700, cursor: saving ? 'wait' : 'pointer', fontSize: '0.85rem' }}>
            {saving ? 'Guardando…' : '💾 Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Port square ───────────────────────────────────────────────────────────────
/**
 * A 22×22 px square representing one physical port.
 * grey dot  = free
 * solid dot = connected (cable color)
 * blue ring = selected source
 * green bg  = available target
 */
function PortSquare({ port, portType, deviceName, isSource, canConnect, onSelect, onHoverPort, isHighlighted, isSameSide }) {
  const [hover, setHover] = useState(false);
  const [tipPos, setTipPos] = useState(null);
  const connected = Boolean(port.connection);
  const cHex  = connected ? colorHex(port.connection.cable_color) : null;
  const isNic = portType === 'component_port';

  // Label shown inside the port square
  const rawLabel = isNic
    ? (port.label || `N${port.port_number}`)
    : String(port.port_number ?? '');
  const portLabel = rawLabel.length > 5 ? rawLabel.slice(0, 5) : rawLabel;

  let bg = 'var(--bg-toolbar)', border = 'var(--border)', cursor = 'pointer';
  if (isSource)        { bg = 'rgba(var(--accent-rgb),.1)'; border = 'var(--accent)'; }
  else if (connected)  { bg = `${cHex}22`; border = cHex; }
  else if (canConnect) { bg = 'var(--conn-banner-bg)'; border = 'var(--conn-banner-bd)'; cursor = 'crosshair'; }

  const labelColor = connected ? cHex : isSource ? 'var(--accent)' : 'var(--text-muted)';

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div
        data-port-id={port.id}
        data-port-type={portType}
        role="button"
        tabIndex={0}
        onClick={() => onSelect(port)}
        onKeyDown={e => e.key === 'Enter' && onSelect(port)}
        onMouseEnter={e => {
          setHover(true);
          onHoverPort && onHoverPort(port.id, port.connection?.other_end ? `${port.connection.other_end.port_type}:${port.connection.other_end.port_id}` : undefined);
          const r = e.currentTarget.getBoundingClientRect();
          setTipPos({ x: r.left + r.width / 2, y: r.top });
        }}
        onMouseLeave={() => { setHover(false); onHoverPort && onHoverPort(null); setTipPos(null); }}
        style={{
          width: isNic ? 'auto' : 22, minWidth: 22, height: 22,
          paddingLeft: isNic ? 4 : 0, paddingRight: isNic ? 4 : 0,
          borderRadius: 4,
          border: `2px solid ${border}`, background: bg,
          cursor, outline: 'none', boxSizing: 'border-box',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'border-color 0.1s, background 0.1s',
          transform: isHighlighted ? 'scale(1.45)' : 'none',
          animation: isHighlighted ? 'nc-pulse 0.65s ease-in-out infinite' : 'none',
          zIndex: isHighlighted ? 5 : 'auto',
          boxShadow: isSource
            ? '0 0 0 3px rgba(var(--accent-rgb),.35)'
            : isHighlighted
            ? undefined
            : hover && connected
            ? '0 0 0 2px var(--error-bd)'
            : hover && canConnect
            ? '0 0 0 2px var(--conn-banner-bd)'
            : 'none',
        }}
      >
        <span style={{
          fontSize: isNic ? '0.38rem' : '0.45rem',
          fontWeight: 700,
          fontFamily: 'monospace',
          lineHeight: 1,
          color: labelColor,
          userSelect: 'none',
          letterSpacing: '-0.02em',
        }}>{portLabel}</span>
      </div>

      {/* Tooltip — position:fixed so it escapes any overflow:hidden ancestor */}
      {hover && tipPos && (
        <div style={{
          position: 'fixed',
          left: tipPos.x, top: tipPos.y - 8,
          transform: 'translate(-50%, -100%)',
          zIndex: 9999, pointerEvents: 'none',
          background: 'rgba(17,24,39,0.93)', color: '#fff', fontSize: '0.7rem',
          padding: '5px 9px', borderRadius: 6, whiteSpace: 'nowrap',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)', lineHeight: 1.65,
        }}>
          <div style={{ fontWeight: 700 }}>{deviceName} · {port.label || `P${port.port_number}`}</div>
          {port.speed && <div style={{ color: '#93c5fd' }}>⚡ {port.speed}</div>}
          {port.mac_address && <div style={{ color: '#fde68a', fontFamily: 'monospace', fontSize: '0.65rem' }}>{port.mac_address}</div>}
          {connected ? (
            <>
              <div style={{ color: isSameSide ? 'var(--warn-bd)' : 'var(--conn-banner-bd)' }}>
                ↔ {port.connection.other_end?.element_name} · {port.connection.other_end?.label || `P${port.connection.other_end?.port_number}`}
                {isSameSide && <span style={{ marginLeft: 5, fontSize: '0.6rem', fontWeight: 700, color: 'var(--warn-bd)', background: 'var(--warn-bg)', padding: '1px 5px', borderRadius: 4 }}>mismo lado</span>}
              </div>
              {port.connection.cable_type && <div style={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}>{port.connection.cable_type}{port.connection.cable_color ? ` · ${port.connection.cable_color}` : ''}{port.connection.cable_length_m ? ` · ${port.connection.cable_length_m}m` : ''}</div>}
              <div style={{ color: 'var(--error-bd)', marginTop: 2, fontSize: '0.65rem' }}>Clic ✂️ desconectar</div>
            </>
          ) : isSource ? (
            <div style={{ color: '#93c5fd' }}>Seleccionado — elige destino →</div>
          ) : canConnect ? (
            <div style={{ color: 'var(--conn-banner-bd)' }}>← Clic para conectar aquí</div>
          ) : (
            <div style={{ color: 'var(--text-muted)' }}>Puerto libre</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Device card (Cuadrícula) ──────────────────────────────────────────────────

function DeviceCard({ dt, device, ports, selectedPort, canConnect, onPortSelect, rows, onHoverPort, onRowsChange, isBlocked, hoveredOtherEndPortId, rightPortIds }) {
  const cardRef = useRef(null);
  const [maxCols, setMaxCols] = useState(99);

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const measure = () => {
      // card inner width = clientWidth minus padding (0.85rem ≈ 14px each side)
      const w = el.clientWidth - 28;
      // each port slot ≈ 25px (22px min-width + 3px gap)
      setMaxCols(Math.max(1, Math.floor(w / 25)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!ports || ports.length === 0) return null;
  const used = ports.filter(p => p.connection).length;
  const desiredCols = rows === 1 ? ports.length : Math.ceil(ports.length / rows);
  const cols = Math.min(desiredCols, maxCols);

  return (
    <div ref={cardRef} style={{
      background: 'var(--bg-toolbar)', border: '1px solid var(--border)', borderRadius: 9,
      padding: '0.65rem 0.85rem', marginBottom: 7,
      boxShadow: 'var(--shadow-sm)',
      opacity: isBlocked ? 0.35 : 1,
      pointerEvents: isBlocked ? 'none' : 'auto',
      filter: isBlocked ? 'grayscale(0.6)' : 'none',
    }} title={isBlocked ? 'Este elemento ya está seleccionado como origen' : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '50%' }}>
          {device.name}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {ports.length > 4 && [1, 2, 3, 4].map(n => (
            <button key={n} onClick={() => onRowsChange && onRowsChange(device.id, n)}
              title={n === 1 ? '1 fila' : `${n} filas`}
              style={{
                width: 16, height: 16, padding: 0, borderRadius: 3, fontSize: '0.5rem',
                fontWeight: rows === n ? 700 : 400, cursor: 'pointer', lineHeight: 1,
                border: rows === n ? '1.5px solid var(--accent)' : '1px solid var(--slate-300)',
                background: rows === n ? 'rgba(var(--accent-rgb),.18)' : 'var(--bg-input)',
                color: rows === n ? 'var(--accent-l)' : 'var(--text-secondary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
              {n}
            </button>
          ))}
          <span style={{
            fontSize: '0.63rem', fontWeight: 700, padding: '1px 7px', borderRadius: 8,
            background: used === ports.length && used > 0 ? 'var(--error-bg)' : used > 0 ? 'var(--amber-50)' : 'var(--conn-banner-bg)',
            color:      used === ports.length && used > 0 ? 'var(--red-600)' : used > 0 ? 'var(--amber-600)' : 'var(--green-600)',
          }}>
            {used}/{ports.length}
          </span>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, auto)`, gap: '10px 3px', justifyContent: 'start' }}>
        {ports.map(port => (
          <PortSquare
            key={port.id}
            port={port}
            portType={dt.portType}
            deviceName={device.name}
            isSource={selectedPort?.id === port.id}
            canConnect={canConnect && !port.connection}
            onSelect={onPortSelect}
            onHoverPort={onHoverPort}
            isHighlighted={hoveredOtherEndPortId != null && hoveredOtherEndPortId === `${dt.portType}:${port.id}`}
            isSameSide={rightPortIds != null && port.connection?.other_end != null && !rightPortIds.has(`${port.connection.other_end.port_type}:${port.connection.other_end.port_id}`)}
          />
        ))}
      </div>
    </div>
  );
}

// ── Cuadrícula view ───────────────────────────────────────────────────────────
// Dynamic-spacing layout: each device card gets a gap below (left) / above (right)
// proportional to the number of cables that exit/enter it. Cables are routed:
//   port → V drop to gap → H to channel edge → bezier in channel → H from channel → V rise to port
// This keeps every cable segment fully visible and never overlapping other cards.

const LANE_STEP = 7;   // px between consecutive cable lanes in a gap
const GAP_PAD   = 10;  // base padding at the start of each gap

function CuadriculaView({ leftGroups, rightGroups, selectedPort, onLeftPortClick, onRightPortClick, onDisconnect, leftRows, rightRows }) {
  const containerRef  = useRef(null);
  const leftColRef    = useRef(null);
  const rightColRef   = useRef(null);
  const leftCardRefs  = useRef({});   // deviceId → wrapper DOM element
  const rightCardRefs = useRef({});

  const [cables,  setCables]  = useState([]);
  const [tooltip, setTooltip] = useState(null);
  const [svgH,    setSvgH]    = useState(600);
  const [hoveredPortId, setHoveredPortId] = useState(null);
  const [hoveredOtherEndPortId, setHoveredOtherEndPortId] = useState(null);
  const [rowOverrides, setRowOverrides] = useState({});  // deviceId → rows override

  const handleRowOverride = useCallback((deviceId, n) => {
    setRowOverrides(prev => ({ ...prev, [deviceId]: n }));
  }, []);

  // ── All connections originating from the left column ────────────────────────
  const leftConnections = useMemo(() => {
    const list = [];
    leftGroups.forEach(g => {
      g.devices.forEach(d => {
        (g.portsMap[d.id] || []).forEach(port => {
          if (port.connection) {
            const oe = port.connection.other_end;
            list.push({
              leftPortId:   port.id,
              rightPortId:  oe?.port_id,
              rightPortKey: oe ? `${oe.port_type}:${oe.port_id}` : null,
              conn:         port.connection,
              device:       d,
              leftDevId:    d.id,
            });
          }
        });
      });
    });
    return list;
  }, [leftGroups]);

  // ── Map every right portKey → right deviceId ─────────────────────────────────
  // Keys use composite "portType:portId" to avoid cross-table ID collisions
  // (e.g. switch_port.id=5 vs panel_port.id=5 are different keys).
  const rightPortToDevId = useMemo(() => {
    const m = {};
    rightGroups.forEach(g => {
      g.devices.forEach(d => {
        (g.portsMap[d.id] || []).forEach(p => { m[`${g.portType}:${p.id}`] = d.id; });
      });
    });
    return m;
  }, [rightGroups]);

  // ── Set of all right-side port keys (for same-side connection detection) ───────
  const rightPortIds = useMemo(() => new Set(Object.keys(rightPortToDevId)), [rightPortToDevId]);

  // ── Only connections whose other_end is actually in the right column ──────────
  // Filters out same-side cables (e.g. switch→switch on the left) that would
  // otherwise produce ghost bezier lines pointing to random positions.
  const visibleLeftConnections = useMemo(() =>
    leftConnections.filter(c => c.rightPortKey != null && rightPortToDevId[c.rightPortKey] !== undefined),
  [leftConnections, rightPortToDevId]);

  // ── Cable counts per device (drives gap sizing) ──────────────────────────────
  const exitCount = useMemo(() => {
    const m = {};
    visibleLeftConnections.forEach(c => { m[c.leftDevId] = (m[c.leftDevId] || 0) + 1; });
    return m;
  }, [visibleLeftConnections]);

  const entryCount = useMemo(() => {
    const m = {};
    visibleLeftConnections.forEach(({ rightPortKey }) => {
      const dId = rightPortToDevId[rightPortKey];
      if (dId) m[dId] = (m[dId] || 0) + 1;
    });
    return m;
  }, [visibleLeftConnections, rightPortToDevId]);

  // ── Compute cable geometries after each render ───────────────────────────────
  useLayoutEffect(() => {
    if (!containerRef.current || !leftColRef.current || !rightColRef.current) return;

    // Clean stale card refs (entries for devices no longer in the DOM)
    const leftDevIds = new Set(leftGroups.flatMap(g => g.devices.map(d => d.id)));
    const rightDevIds = new Set(rightGroups.flatMap(g => g.devices.map(d => d.id)));
    Object.keys(leftCardRefs.current).forEach(k => {
      if (!leftDevIds.has(Number(k))) delete leftCardRefs.current[k];
    });
    Object.keys(rightCardRefs.current).forEach(k => {
      if (!rightDevIds.has(Number(k))) delete rightCardRefs.current[k];
    });

    const compute = () => {
      if (!containerRef.current || !leftColRef.current || !rightColRef.current) return;
      const cR  = containerRef.current.getBoundingClientRect();
      const lCR = leftColRef.current.getBoundingClientRect();
      const rCR = rightColRef.current.getBoundingClientRect();
      const lExit  = lCR.right - cR.left;
      const rEntry = rCR.left  - cR.left;
      const midX   = (lExit + rEntry) / 2;
      setSvgH(Math.max(cR.height + 60, 300));

      // Per-device lane counters (reset on each compute)
      const lLane = {};
      const rLane = {};

      const nc = visibleLeftConnections.map(({ leftPortId, rightPortId, rightPortKey, conn, device, leftDevId }) => {
        const lEl = leftColRef.current.querySelector(`[data-port-id="${leftPortId}"]`);
        const rEl = rightPortId ? rightColRef.current.querySelector(`[data-port-id="${rightPortId}"]`) : null;
        if (!lEl || !rEl) return null;

        const lr = lEl.getBoundingClientRect();
        const rr = rEl.getBoundingClientRect();
        const x1 = lr.left + lr.width / 2 - cR.left;
        const y1 = lr.bottom - cR.top;
        const x2 = rr.left + rr.width / 2 - cR.left;
        const y2 = rr.bottom - cR.top;

        const rightDevId = rightPortToDevId[rightPortKey];

        // Assign lanes (order among cables sharing the same source/dest device gap)
        const li = lLane[leftDevId]  ?? 0;  lLane[leftDevId]  = li + 1;
        const ri = rLane[rightDevId] ?? 0;  rLane[rightDevId] = ri + 1;

        // Exit point: bottom of source card + lane offset (cables fan out downward)
        let gapLy = y1;
        const lCardEl = leftCardRefs.current[leftDevId];
        if (lCardEl) {
          const cr = lCardEl.getBoundingClientRect();
          gapLy = cr.bottom - cR.top + GAP_PAD + li * LANE_STEP;
        }

        // Entry point: bottom of dest card + lane offset (cables fan in from below, symmetrical with left)
        let gapRy = y2;
        const rCardEl = rightCardRefs.current[rightDevId];
        if (rCardEl) {
          const cr = rCardEl.getBoundingClientRect();
          gapRy = cr.bottom - cR.top + GAP_PAD + ri * LANE_STEP;
        }

        return {
          key: `${leftPortId}-${rightPortId ?? 'x'}`,
          x1, y1, x2, y2, gapLy, gapRy, lExit, rEntry, midX,
          conn, device,
        };
      }).filter(Boolean);

      // Deduplicate: each physical connection drawn only once (prevents X-pattern
      // when same device type appears on both sides and both endpoints are visible)
      const connSeen = new Set();
      const unique = nc.filter(c => {
        if (connSeen.has(c.conn.id)) return false;
        connSeen.add(c.conn.id);
        return true;
      });

      setCables(unique);
    };

    compute();
    // Re-compute after browser finishes pending layout (catches async settling)
    const rafId = requestAnimationFrame(compute);
    const lc = leftColRef.current;
    const rc = rightColRef.current;
    lc.addEventListener('scroll', compute);
    rc.addEventListener('scroll', compute);
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute);
    return () => {
      cancelAnimationFrame(rafId);
      lc?.removeEventListener('scroll', compute);
      rc?.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute);
    };
  }, [visibleLeftConnections, leftGroups, rightGroups, rightPortToDevId, leftRows, rightRows, rowOverrides]);

  // ── Path: port-bottom → V drop → H to channel → bezier → H from channel → V rise → port-bottom
  const buildDecorativePath = ({ x1, y1, x2, y2, gapLy, gapRy, lExit, rEntry, midX }) => {
    return (
      `M ${x1} ${y1}` +          // start at source port bottom-center
      ` V ${gapLy}` +             // drop into cable-tray below source card
      ` H ${lExit}` +             // horizontal to channel left edge
      ` C ${midX} ${gapLy} ${midX} ${gapRy} ${rEntry} ${gapRy}` +  // bezier through channel
      ` H ${x2}` +                // horizontal in cable-tray to dest port x
      ` V ${y2}`                  // rise up to dest port bottom-center
    );
  };

  // ── Hit path: bezier in channel only — never covers port areas ───────────────
  const buildHitPath = ({ gapLy, gapRy, lExit, rEntry, midX }) => {
    return `M ${lExit} ${gapLy} C ${midX} ${gapLy} ${midX} ${gapRy} ${rEntry} ${gapRy}`;
  };

  // ── Column renderer (shared left/right logic) ────────────────────────────────
  const renderColumn = (groups, side) => {
    const isLeft    = side === 'left';
    const cardRefs  = isLeft ? leftCardRefs : rightCardRefs;
    const countMap  = isLeft ? exitCount : entryCount;
    const rows      = isLeft ? leftRows : rightRows;

    return groups.map(g => {
      if (g.devices.length === 0) return null;
      const dt = DEVICE_TYPES[g.typeKey];
      return (
        <div key={g.typeKey}>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, color: dt.color, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5, marginTop: 4 }}>
            {dt.icon} {dt.label}
          </div>
          {g.devices.map(d => {
            const n = countMap[d.id] || 0;
            const spacerH = n > 0 ? GAP_PAD * 2 + n * LANE_STEP : 0;
            const effectiveRows = rowOverrides[d.id] || rows;
            return (
              <React.Fragment key={d.id}>
                <div ref={el => { cardRefs.current[d.id] = el; }}>
                  <DeviceCard
                    dt={dt}
                    device={d}
                    ports={g.portsMap[d.id] || []}
                    selectedPort={isLeft ? selectedPort : null}
                    canConnect={isLeft ? false : Boolean(selectedPort)}
                    isBlocked={!isLeft && selectedPort?.device?.id === d.id}
                    rows={effectiveRows}
                    onHoverPort={(id, otherId) => { setHoveredPortId(id); setHoveredOtherEndPortId(otherId ?? null); }}
                    hoveredOtherEndPortId={hoveredOtherEndPortId}
                    rightPortIds={isLeft ? rightPortIds : null}
                    onRowsChange={handleRowOverride}
                    onPortSelect={port => {
                      if (port.connection) {
                        onDisconnect({
                          id: port.connection.id,
                          deviceA: isLeft ? d.name : (port.connection.other_end?.element_name),
                          portA:   isLeft ? (port.label || `P${port.port_number}`) : (port.connection.other_end?.label || `P${port.connection.other_end?.port_number}`),
                          deviceB: isLeft ? (port.connection.other_end?.element_name) : d.name,
                          portB:   isLeft ? (port.connection.other_end?.label || `P${port.connection.other_end?.port_number}`) : (port.label || `P${port.port_number}`),
                          cable_type:     port.connection.cable_type,
                          cable_color:    port.connection.cable_color,
                          cable_length_m: port.connection.cable_length_m,
                          notes:          port.connection.notes,
                        });
                      } else if (isLeft) {
                        onLeftPortClick(port, g.portType, d.name, d, g.typeKey);
                      } else if (selectedPort) {
                        onRightPortClick(port, g.portType, d.name, d, g.typeKey);
                      }
                    }}
                  />
                </div>

                {/* Spacer BELOW card — cable tray zone for departing/arriving cables */}
                {spacerH > 0 && <div style={{ height: spacerH }} />}
              </React.Fragment>
            );
          })}
        </div>
      );
    });
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'flex', gap: 200, alignItems: 'stretch', flex: 1, minHeight: 0, overflow: 'hidden' }}>

      {/* ── Left column (z:3 — above SVG so ports remain clickable) ── */}
      <div ref={leftColRef} style={{ flex: 1, minWidth: 0, overflowY: 'auto', maxHeight: '100%', zIndex: 3, position: 'relative' }}>
        {renderColumn(leftGroups, 'left')}
      </div>

      {/* ── Decorative SVG (z:2, behind columns z:3) — cable visible in open channel, not over card content ── */}
      <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: svgH, overflow: 'visible', zIndex: 2, pointerEvents: 'none' }}>
        <defs>
          {CABLE_COLORS.map(c => (
            <marker key={c.value} id={`nc-a-${c.value}`} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
              <polygon points="0 0,7 3.5,0 7" fill={c.hex} />
            </marker>
          ))}
          <marker id="nc-a-default" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <polygon points="0 0,7 3.5,0 7" fill="#9ca3af" />
          </marker>
        </defs>
        {cables.map(cable => {
          const cHex     = colorHex(cable.conn.cable_color);
          const markerId = cable.conn.cable_color ? `nc-a-${cable.conn.cable_color}` : 'nc-a-default';
          const isHl     = hoveredPortId && (String(cable.key).startsWith(String(hoveredPortId) + '-') || String(cable.key).endsWith('-' + String(hoveredPortId)));
          return (
            <React.Fragment key={cable.key}>
              {isHl && (
                <path
                  d={buildDecorativePath(cable)}
                  stroke={cHex} strokeWidth={8} fill="none" opacity={0.25}
                  strokeLinecap="round"
                />
              )}
              <path
                d={buildDecorativePath(cable)}
                stroke={cHex} strokeWidth={isHl ? 4 : 2} fill="none"
                markerEnd={`url(#${markerId})`} opacity={isHl ? 1 : 0.85}
                style={{ transition: 'stroke-width 0.15s, opacity 0.15s' }}
              />
            </React.Fragment>
          );
        })}
      </svg>

      {/* ── Hit-area SVG (z:11, parent pointer-events:none) — channel bezier only ── */}
      <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: svgH, overflow: 'visible', zIndex: 11, pointerEvents: 'none' }}>
        {cables.map(cable => (
          <path key={`hit-${cable.key}`}
            d={buildHitPath(cable)}
            stroke="transparent" strokeWidth={14} fill="none"
            pointerEvents="visibleStroke"
            style={{ cursor: 'pointer' }}
            onMouseEnter={e => {
              const cRect = containerRef.current?.getBoundingClientRect();
              setTooltip({ x: e.clientX - (cRect?.left || 0) + 14, y: e.clientY - (cRect?.top || 0) - 14, conn: cable.conn, device: cable.device });
            }}
            onMouseLeave={() => setTooltip(null)}
            onClick={() => {
              onDisconnect({
                id: cable.conn.id,
                deviceA: cable.device.name, portA: '—',
                deviceB: cable.conn.other_end?.element_name,
                portB:   cable.conn.other_end?.label || `P${cable.conn.other_end?.port_number}`,
                cable_type: cable.conn.cable_type, cable_color: cable.conn.cable_color, cable_length_m: cable.conn.cable_length_m, notes: cable.conn.notes,
              });
              setTooltip(null);
            }}
          />
        ))}
      </svg>

      {/* ── Right column (z:3 — above SVG so ports remain clickable) ── */}
      <div ref={rightColRef} style={{ flex: 1, minWidth: 0, overflowY: 'auto', maxHeight: '100%', zIndex: 3, position: 'relative' }}>
        {renderColumn(rightGroups, 'right')}
      </div>

      {/* ── Cable hover tooltip ── */}
      {tooltip && (
        <div style={{ position: 'absolute', left: tooltip.x, top: tooltip.y, zIndex: 50, background: 'rgba(17,24,39,0.92)', color: '#fff', fontSize: '0.73rem', padding: '6px 10px', borderRadius: 6, pointerEvents: 'none', maxWidth: 320, lineHeight: 1.6, boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
          <div style={{ fontWeight: 700 }}>{tooltip.conn.cable_type || 'Cable'}{tooltip.conn.cable_color ? ` · ${tooltip.conn.cable_color}` : ''}{tooltip.conn.cable_length_m ? ` · ${tooltip.conn.cable_length_m}m` : ''}</div>
          {tooltip.conn.other_end && <div style={{ color: '#93c5fd' }}>↔ {tooltip.conn.other_end.element_name} · {tooltip.conn.other_end.label || `P${tooltip.conn.other_end.port_number}`}</div>}
          <div style={{ color: 'var(--error-bd)', marginTop: 2, fontSize: '0.68rem' }}>Clic para ✂️ desconectar</div>
        </div>
      )}

      {cables.length === 0 && visibleLeftConnections.length === 0 && (
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: '30%', color: 'var(--text-muted)', fontSize: '0.88rem', pointerEvents: 'none', textAlign: 'center' }}>
          Sin conexiones entre los dispositivos seleccionados.<br />
          <span style={{ fontSize: '0.78rem' }}>Haz clic en un puerto libre de la izquierda y luego en uno de la derecha.</span>
        </div>
      )}
    </div>
  );
}

// ── Lista view ────────────────────────────────────────────────────────────────

function PortRow({ port, portType, deviceName, isSource, canConnect, onSelect, isHighlighted, onHoverPort }) {
  const conn = port.connection;
  const cHex = conn ? colorHex(conn.cable_color) : null;
  let bg = 'var(--bg-toolbar)', border = 'var(--border)', cursor = 'pointer';
  if (isSource)        { bg = 'rgba(var(--accent-rgb),.1)'; border = 'var(--accent)'; }
  else if (isHighlighted) { bg = 'var(--warn-bg)'; border = 'var(--port-selected-bd)'; }
  else if (conn)       { bg = `${cHex}18`; border = cHex; }
  else if (canConnect) { bg = 'var(--conn-banner-bg)'; border = 'var(--conn-banner-bd)'; cursor = 'crosshair'; }

  return (
    <div role="button" tabIndex={0}
      onClick={onSelect} onKeyDown={e => e.key === 'Enter' && onSelect()}
      onMouseEnter={() => onHoverPort && onHoverPort(port.id, port.connection?.other_end ? `${port.connection.other_end.port_type}:${port.connection.other_end.port_id}` : undefined)}
      onMouseLeave={() => onHoverPort && onHoverPort(null, null)}
      style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 10px', marginBottom: 3, borderRadius: 6, border: `2px solid ${border}`, background: bg, cursor, outline: 'none', userSelect: 'none', transition: 'border-color 0.12s, background 0.12s', animation: isHighlighted ? 'nc-pulse 0.65s ease-in-out infinite' : 'none' }}>
      <span style={{ minWidth: 28, textAlign: 'center', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-primary)', background: 'var(--border)', borderRadius: 4, padding: '1px 4px' }}>
        {port.label || `P${port.port_number}`}
      </span>
      {port.speed && <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: 'rgba(var(--accent-rgb),.12)', color: 'var(--accent-dark)' }}>{port.speed}</span>}
      {conn ? (
        <>
          <span style={{ color: cHex, fontSize: '0.9rem' }}>●</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--green-600)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conn.other_end?.element_name}</span>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{conn.other_end?.label || `P${conn.other_end?.port_number}`}</span>
          <span style={{ fontSize: '0.75rem' }}>✂️</span>
        </>
      ) : isSource ? (
        <><span style={{ fontSize: '0.8rem', color: 'var(--accent)' }}>→</span><span style={{ fontSize: '0.75rem', color: 'var(--accent)', flex: 1, fontWeight: 600 }}>Seleccionado — elige destino</span><span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Esc</span></>
      ) : canConnect ? (
        <span style={{ fontSize: '0.72rem', color: 'var(--green-600)', flex: 1, fontWeight: 600 }}>← Conectar aquí</span>
      ) : (
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', flex: 1 }}>Libre</span>
      )}
    </div>
  );
}

function ListaColumn({ groups, side, selectedPort, onPortClick, onDisconnect, sourceDeviceId }) {
  const isLeft = side === 'left';
  const [hoveredOtherEndPortId, setHoveredOtherEndPortId] = useState(null);
  const handleHoverPort = useCallback((id, otherId) => {
    setHoveredOtherEndPortId(otherId ?? null);
  }, []);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.85rem', paddingBottom: '0.5rem', borderBottom: '2px solid var(--border)', flexShrink: 0, zIndex: 10, background: 'var(--bg-card)' }}>
        <h2 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>{isLeft ? '◀ Origen' : 'Destino ▶'}</h2>
        {groups.map(g => (
          <span key={g.typeKey} style={{ fontSize: '0.72rem', background: `${DEVICE_TYPES[g.typeKey].color}20`, color: DEVICE_TYPES[g.typeKey].color, padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
            {DEVICE_TYPES[g.typeKey].icon} {g.devices.length}
          </span>
        ))}
      </div>
      <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {groups.map(g => g.devices.map(device => {
          const ports = g.portsMap[device.id] || [];
          if (!ports.length) return null;
          const used = ports.filter(p => p.connection).length;
          return (
            <div key={`${g.typeKey}-${device.id}`}
            title={!isLeft && device.id === sourceDeviceId ? 'Este elemento ya está seleccionado como origen' : undefined}
            style={{ background: 'var(--bg-toolbar)', border: '1px solid var(--border)', borderRadius: 10, padding: '0.9rem', marginBottom: '0.85rem', boxShadow: 'var(--shadow-sm)', opacity: !isLeft && device.id === sourceDeviceId ? 0.35 : 1, pointerEvents: !isLeft && device.id === sourceDeviceId ? 'none' : 'auto', filter: !isLeft && device.id === sourceDeviceId ? 'grayscale(0.6)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.65rem' }}>
                <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>{DEVICE_TYPES[g.typeKey].icon}</span> {device.name}
                </div>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '2px 9px', borderRadius: 10, color: '#fff', background: used === ports.length && used > 0 ? 'var(--red-600)' : used > 0 ? 'var(--amber-600)' : 'var(--green-600)' }}>
                  {used === ports.length && used > 0 ? 'LLENO' : used > 0 ? 'PARCIAL' : 'LIBRE'}
                </span>
              </div>
              {ports.map(port => (
                <PortRow key={port.id} port={port} portType={g.portType} deviceName={device.name}
                  isSource={selectedPort?.id === port.id}
                  canConnect={!isLeft && Boolean(selectedPort) && !port.connection}
                  isHighlighted={hoveredOtherEndPortId != null && hoveredOtherEndPortId === `${g.portType}:${port.id}`}
                  onHoverPort={handleHoverPort}
                  onSelect={() => {
                    const conn = port.connection;
                    if (conn) {
                      onDisconnect({
                        id: conn.id,
                        deviceA: conn.other_end?.element_name || device.name, portA: conn.other_end?.label || `P${conn.other_end?.port_number}`,
                        deviceB: device.name, portB: port.label || `P${port.port_number}`,
                        cable_type: conn.cable_type, cable_color: conn.cable_color, cable_length_m: conn.cable_length_m, notes: conn.notes,
                      });
                    } else {
                      onPortClick(port, g.portType, device.name, device, g.typeKey);
                    }
                  }}
                />
              ))}
            </div>
          );
        }))}
      </div>
    </div>
  );
}

// ── Diagrama view ─────────────────────────────────────────────────────────────

function DiagramView({ leftGroups, rightGroups, onDisconnect }) {
  const containerRef = useRef(null);
  const leftColRef   = useRef(null);
  const rightColRef  = useRef(null);
  const [cables,  setCables]  = useState([]);
  const [tooltip, setTooltip] = useState(null);
  const [svgH,    setSvgH]    = useState(500);

  const connections = useMemo(() => {
    const list = [];
    leftGroups.forEach(g => g.devices.forEach(d => {
      (g.portsMap[d.id] || []).forEach(p => {
        if (p.connection) list.push({ port: p, device: d, typeKey: g.typeKey, conn: p.connection });
      });
    }));
    return list;
  }, [leftGroups]);

  // Build set of composite right-side port keys to filter out same-side ghost cables
  const rightPortKeys = useMemo(() => {
    const s = new Set();
    rightGroups.forEach(g => g.devices.forEach(d => {
      (g.portsMap[d.id] || []).forEach(p => { s.add(`${g.portType}:${p.id}`); });
    }));
    return s;
  }, [rightGroups]);

  const visibleConnections = useMemo(() =>
    connections.filter(({ conn }) => {
      const oe = conn.other_end;
      return oe && rightPortKeys.has(`${oe.port_type}:${oe.port_id}`);
    }),
  [connections, rightPortKeys]);

  useLayoutEffect(() => {
    if (!containerRef.current || !leftColRef.current || !rightColRef.current) return;

    const compute = () => {
      if (!containerRef.current || !leftColRef.current || !rightColRef.current) return;
      const cRect = containerRef.current.getBoundingClientRect();
      setSvgH(Math.max(cRect.height, 200));

      const nc = visibleConnections.map(({ port, conn }) => {
        const lEl = leftColRef.current.querySelector(`[data-port-id="${port.id}"]`);
        const rEl = conn.other_end?.port_id ? rightColRef.current.querySelector(`[data-port-id="${conn.other_end.port_id}"]`) : null;
        if (!lEl || !rEl) return null;
        const lr = lEl.getBoundingClientRect();
        const rr = rEl.getBoundingClientRect();
        return {
          key: `${port.id}-${conn.other_end.port_id}`,
          x1: lr.right - cRect.left, y1: lr.top - cRect.top + lr.height / 2,
          x2: rr.left  - cRect.left, y2: rr.top - cRect.top + rr.height / 2,
          conn, port,
        };
      }).filter(Boolean);

      // Deduplicate: each physical connection drawn only once
      const connSeen = new Set();
      const unique = nc.filter(c => {
        if (connSeen.has(c.conn.id)) return false;
        connSeen.add(c.conn.id);
        return true;
      });

      setCables(unique);
    };

    compute();
    const rafId = requestAnimationFrame(compute);
    const lc = leftColRef.current;
    const rc = rightColRef.current;
    lc.addEventListener('scroll', compute);
    rc.addEventListener('scroll', compute);
    window.addEventListener('resize', compute);

    return () => {
      cancelAnimationFrame(rafId);
      lc?.removeEventListener('scroll', compute);
      rc?.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
    };
  }, [visibleConnections, leftGroups, rightGroups]);

  const colH = '100%';
  // Compact cards so cables are visible in the diagram view
  const colWidth = '44%';
  const portStyle = {
    background: 'var(--bg-toolbar)',
    borderRadius: 5,
    padding: '2px 6px',
    marginBottom: 3,
    fontSize: '0.72rem',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    maxWidth: 230,
    minWidth: 150,
    boxShadow: 'var(--shadow-sm)',
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'flex', gap: 0, alignItems: 'stretch', flex: 1, minHeight: 0 }}>
      {/* Left */}
      <div ref={leftColRef} style={{ flex: `0 0 ${colWidth}`, maxWidth: colWidth, minWidth: 0, overflowY: 'auto', maxHeight: colH, paddingRight: 8, zIndex: 1 }}>
        {leftGroups.map(g => g.devices.map(d => {
          const ports = (g.portsMap[d.id] || []).filter(p => p.connection);
          if (!ports.length) return null;
          return (
            <div key={d.id} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: DEVICE_TYPES[g.typeKey].color, marginBottom: 4 }}>
                {DEVICE_TYPES[g.typeKey].icon} {d.name}
              </div>
              {ports.map(p => (
                <div key={p.id} data-port-id={p.id} style={{ ...portStyle, border: `2px solid ${colorHex(p.connection.cable_color)}` }}>
                  <span style={{ color: colorHex(p.connection.cable_color), fontWeight: 700 }}>●</span>
                  <span style={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.label || `P${p.port_number}`}</span>
                  {p.speed && <span style={{ fontSize: '0.62rem', background: 'rgba(var(--accent-rgb),.12)', color: 'var(--accent-dark)', padding: '1px 4px', borderRadius: 3, whiteSpace: 'nowrap' }}>{p.speed}</span>}
                </div>
              ))}
            </div>
          );
        }))}
      </div>

      {/* SVG cables (decorative, no pointer events) */}
      <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: svgH, overflow: 'visible', zIndex: 2, pointerEvents: 'none' }}>
        <defs>
          {CABLE_COLORS.map(c => (
            <marker key={c.value} id={`ncD-a-${c.value}`} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
              <polygon points="0 0,7 3.5,0 7" fill={c.hex} />
            </marker>
          ))}
          <marker id="ncD-a-default" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <polygon points="0 0,7 3.5,0 7" fill="#9ca3af" />
          </marker>
        </defs>
        {cables.map(({ key, x1, y1, x2, y2, conn }) => {
          const cHex     = colorHex(conn.cable_color);
          const markerId = conn.cable_color ? `ncD-a-${conn.cable_color}` : `ncD-a-default`;
          const midX     = (x1 + x2) / 2;
          const d        = `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
          return (
            <path key={key} d={d} stroke={cHex} strokeWidth={2.5} fill="none"
              markerEnd={`url(#${markerId})`} opacity={0.7} />
          );
        })}
      </svg>

      {/* SVG hit-areas (interactive) */}
      <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: svgH, overflow: 'visible', zIndex: 3, pointerEvents: 'none' }}>
        {cables.map(({ key, x1, y1, x2, y2, conn, port }) => {
          const midX = (x1 + x2) / 2;
          const d    = `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
          return (
            <path key={`hit-${key}`} d={d} stroke="transparent" strokeWidth={14} fill="none"
              pointerEvents="visibleStroke"
              style={{ cursor: 'pointer' }}
              onMouseEnter={e => {
                const cRect = containerRef.current?.getBoundingClientRect();
                setTooltip({ x: e.clientX - (cRect?.left || 0) + 14, y: e.clientY - (cRect?.top || 0) - 14, conn, port });
              }}
              onMouseLeave={() => setTooltip(null)}
              onClick={() => {
                onDisconnect({ id: conn.id, deviceA: '?', portA: port.label || `P${port.port_number}`, deviceB: conn.other_end?.element_name, portB: conn.other_end?.label || `P${conn.other_end?.port_number}`, cable_type: conn.cable_type, cable_color: conn.cable_color, cable_length_m: conn.cable_length_m, notes: conn.notes });
                setTooltip(null);
              }}
            />
          );
        })}
      </svg>

      {/* Right */}
      <div ref={rightColRef} style={{ flex: `0 0 ${colWidth}`, maxWidth: colWidth, minWidth: 0, overflowY: 'auto', maxHeight: colH, paddingLeft: 8, marginLeft: 'auto', zIndex: 1 }}>
        {rightGroups.map(g => g.devices.map(d => {
          const ports = (g.portsMap[d.id] || []).filter(p => p.connection);
          if (!ports.length) return null;
          return (
            <div key={d.id} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: DEVICE_TYPES[g.typeKey].color, marginBottom: 4 }}>
                {DEVICE_TYPES[g.typeKey].icon} {d.name}
              </div>
              {ports.map(p => (
                <div key={p.id} data-port-id={p.id} style={{ ...portStyle, border: `2px solid ${colorHex(p.connection.cable_color)}` }}>
                  <span style={{ color: colorHex(p.connection.cable_color), fontWeight: 700 }}>●</span>
                  <span style={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.label || `P${p.port_number}`}</span>
                  {p.speed && <span style={{ fontSize: '0.62rem', background: 'rgba(var(--accent-rgb),.12)', color: 'var(--accent-dark)', padding: '1px 4px', borderRadius: 3, whiteSpace: 'nowrap' }}>{p.speed}</span>}
                  {p.mac_address && <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{p.mac_address}</span>}
                </div>
              ))}
            </div>
          );
        }))}
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div style={{ position: 'absolute', left: tooltip.x, top: tooltip.y, zIndex: 50, background: 'rgba(17,24,39,0.92)', color: '#fff', fontSize: '0.73rem', padding: '6px 10px', borderRadius: 6, pointerEvents: 'none', lineHeight: 1.6, boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
          <div>{tooltip.conn.cable_type || 'Cable'}{tooltip.conn.cable_color ? ` · ${tooltip.conn.cable_color}` : ''}{tooltip.conn.cable_length_m ? ` · ${tooltip.conn.cable_length_m}m` : ''}</div>
          <div style={{ color: 'var(--conn-banner-bd)' }}>↔ {tooltip.conn.other_end?.element_name} · {tooltip.conn.other_end?.label || `P${tooltip.conn.other_end?.port_number}`}</div>
          <div style={{ color: 'var(--error-bd)', fontSize: '0.68rem' }}>Clic ✂️ desconectar</div>
        </div>
      )}

      {cables.length === 0 && connections.length === 0 && (
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: '30%', color: 'var(--text-muted)', fontSize: '0.88rem', pointerEvents: 'none', textAlign: 'center' }}>
          Sin conexiones entre los dispositivos seleccionados.
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function NetworkCanvas({ companyId }) {
  const [leftTypes,  setLeftTypes]  = useState(['switch']);
  const [rightTypes, setRightTypes] = useState(['server_nic', 'panel', 'server_bmc']);

  // Device filters (IDs)
  const [leftFilterIds,  setLeftFilterIds]  = useState([]);
  const [rightFilterIds, setRightFilterIds] = useState([]);

  // { [typeKey]: { devices: [], portsMap: { id: [] } } }
  const [loaded,  setLoaded]  = useState({});
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const [view, setView] = useState('cuadricula');

  const [selectedPort, setSelectedPort] = useState(null); // { ...port, typeKey, deviceName, device }
  const [connecting,   setConnecting]   = useState(null); // { destPort, destMeta }
  const [disconnecting,setDisconnecting]= useState(null);

  // Port grid rows per side (1 = single row, 2/3/4 = multi-row)
  const [leftRows,  setLeftRows]  = useState(1);
  const [rightRows, setRightRows] = useState(1);

  // Connect mode: false = solo visualizar conexiones existentes, true = permite hacer nuevas conexiones
  const [connectMode, setConnectMode] = useState(false);

  // Design mode: oculta selectores para maximizar espacio de trabajo
  const [designMode, setDesignMode] = useState(false);

  // ── Load ──────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setError(null);
    const allTypes = [...new Set([...leftTypes, ...rightTypes])];
    const next = {};
    try {
      await Promise.all(allTypes.map(async typeKey => {
        const dt = DEVICE_TYPES[typeKey];
        const devices = await dt.load(companyId);
        const portsMap = {};
        await Promise.all(devices.map(async d => {
          try { portsMap[d.id] = await dt.loadPorts(d.id, companyId); }
          catch { portsMap[d.id] = []; }
        }));
        next[typeKey] = { devices, portsMap };
      }));
      setLoaded(next);
    } catch (e) {
      setError(e.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, leftTypes.join(','), rightTypes.join(',')]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const k = e => { if (e.key === 'Escape') setSelectedPort(null); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, []);

  // ── Groups ────────────────────────────────────────────────────────────────

  function buildGroups(types) {
    return types.map(typeKey => ({
      typeKey,
      portType:  DEVICE_TYPES[typeKey].portType,
      devices:   [...(loaded[typeKey]?.devices  || [])].sort((a, b) => a.name.localeCompare(b.name, 'es', { numeric: true })),
      portsMap:  loaded[typeKey]?.portsMap || {},
    }));
  }

  const leftGroups  = useMemo(() => buildGroups(leftTypes),  [leftTypes,  loaded]); // eslint-disable-line
  const rightGroups = useMemo(() => buildGroups(rightTypes), [rightTypes, loaded]); // eslint-disable-line

  // Helpers to filter groups by selected IDs
  const filterGroupsByIds = useCallback((groups, ids) => {
    if (!ids.length) return groups;
    const keep = new Set(ids);
    return groups.map(g => {
      const devices = g.devices.filter(d => keep.has(d.id));
      const portsMap = {};
      devices.forEach(d => { portsMap[d.id] = g.portsMap[d.id] || []; });
      return { ...g, devices, portsMap };
    });
  }, []);

  // Collect connected element IDs from a side
  const collectConnectedIds = useCallback(groups => {
    const ids = new Set();
    groups.forEach(g => g.devices.forEach(d => {
      (g.portsMap[d.id] || []).forEach(p => {
        if (p.connection?.other_end?.element_id) ids.add(p.connection.other_end.element_id);
      });
    }));
    return ids;
  }, []);

  // Filter out devices that have zero connected ports (for "Solo conectados" mode)
  const filterConnectedOnly = useCallback(groups => {
    return groups.map(g => {
      const devices = g.devices.filter(d => {
        const ports = g.portsMap[d.id] || [];
        return ports.some(p => p.connection);
      });
      const portsMap = {};
      devices.forEach(d => { portsMap[d.id] = g.portsMap[d.id] || []; });
      return { ...g, devices, portsMap };
    });
  }, []);

  // Apply self-filter then cross-filter by connections coming from the opposite side selection
  const leftSelf  = useMemo(() => filterGroupsByIds(leftGroups,  leftFilterIds),  [leftGroups,  leftFilterIds,  filterGroupsByIds]);
  const rightSelf = useMemo(() => filterGroupsByIds(rightGroups, rightFilterIds), [rightGroups, rightFilterIds, filterGroupsByIds]);

  const leftTargets  = useMemo(() => collectConnectedIds(leftSelf),  [leftSelf,  collectConnectedIds]);
  const rightTargets = useMemo(() => collectConnectedIds(rightSelf), [rightSelf, collectConnectedIds]);

  const leftFiltered = useMemo(() => {
    if (connectMode) return leftSelf;  // en modo conexión mostrar todo
    let result = leftSelf;
    if (rightFilterIds.length) {
      result = filterGroupsByIds(result, Array.from(rightTargets));
    }
    return filterConnectedOnly(result);  // solo dispositivos con al menos 1 conexión
  }, [leftSelf, rightFilterIds.length, rightTargets, filterGroupsByIds, connectMode, filterConnectedOnly]);

  const rightFiltered = useMemo(() => {
    if (connectMode) return rightSelf;  // en modo conexión mostrar todo
    let result = rightSelf;
    if (leftFilterIds.length) {
      result = filterGroupsByIds(result, Array.from(leftTargets));
    }
    return filterConnectedOnly(result);  // solo dispositivos con al menos 1 conexión
  }, [rightSelf, leftFilterIds.length, leftTargets, filterGroupsByIds, connectMode, filterConnectedOnly]);

  const leftFilterOptions  = useMemo(() => leftGroups.flatMap(g => g.devices.map(d => ({ id: d.id, name: d.name }))).sort((a, b) => a.name.localeCompare(b.name, 'es', { numeric: true })), [leftGroups]);
  const rightFilterOptions = useMemo(() => rightGroups.flatMap(g => g.devices.map(d => ({ id: d.id, name: d.name }))).sort((a, b) => a.name.localeCompare(b.name, 'es', { numeric: true })), [rightGroups]);

  // ── Stats ─────────────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    let tl = 0, cl = 0, tr = 0, cr = 0;
    leftFiltered.forEach(g  => g.devices.forEach(d => { const p = g.portsMap[d.id]||[]; tl+=p.length; cl+=p.filter(x=>x.connection).length; }));
    rightFiltered.forEach(g => g.devices.forEach(d => { const p = g.portsMap[d.id]||[]; tr+=p.length; cr+=p.filter(x=>x.connection).length; }));
    return { tl, cl, tr, cr };
  }, [leftFiltered, rightFiltered]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleLeftPortClick = (port, portType, deviceName, device, typeKey) => {
    if (selectedPort?.id === port.id) { setSelectedPort(null); return; }
    setSelectedPort({ ...port, typeKey, portType, deviceName, device });
  };

  const handleRightPortClick = (port, portType, deviceName, device, typeKey) => {
    if (!selectedPort) return;
    // Prevent connecting a device to itself
    if (device.id === selectedPort.device?.id) return;
    setConnecting({
      destPort: port,
      destMeta: { type: typeKey, portType, deviceName, device },
    });
  };

  const handleConnectSaved = async () => {
    setConnecting(null); setSelectedPort(null);
    await load();
  };

  const handleDisconnectDone = async () => {
    setDisconnecting(null);
    await load();
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (!companyId) {
    return (
      <div style={{ padding: '2rem', color: 'var(--text-muted)', textAlign: 'center' }}>
        Selecciona una empresa para ver el canvas de red.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden', fontFamily: 'system-ui, sans-serif', boxSizing: 'border-box', background: 'var(--bg-card)', color: 'var(--text-primary)' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '0.7rem 14px', marginBottom: 0, gap: '1rem', flexWrap: 'wrap', flexShrink: 0, borderBottom: '1px solid var(--border)' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 10 }}>
            🔀 Canvas de Red
          </h1>
          <p style={{ margin: '0.3rem 0 0', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            Cableado visual · Switches ↔ Patch Panels ↔ Servidores (NIC)
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {!loading && (
            <span style={{ fontSize: '0.75rem', background: 'var(--bg-toolbar)', padding: '4px 10px', borderRadius: 20, color: 'var(--text-secondary)' }}>
              {stats.cl}/{stats.tl} ◀&nbsp;&nbsp;{stats.cr}/{stats.tr} ▶
            </span>
          )}
          {/* Toggle modo visualización / conexión */}
          <button
            onClick={() => { setConnectMode(m => !m); setSelectedPort(null); }}
            title={connectMode ? 'Modo conexión activo — clic para volver a solo visualizar' : 'Clic para activar modo conexión'}
            style={{
              padding: '0.4rem 0.85rem', borderRadius: 7, fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
              border: connectMode ? '1.5px solid var(--green-600)' : '1px solid var(--slate-300)',
              background: connectMode ? 'var(--conn-banner-bg)' : 'var(--bg-input)',
              color: connectMode ? 'var(--green-600)' : 'var(--text-secondary)',
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
            {connectMode ? '🔌 Modo conexión' : '🔍 Solo conectados'}
          </button>
          <div style={{ display: 'flex', border: '1px solid var(--slate-300)', borderRadius: 7, overflow: 'hidden' }}>
            {[['cuadricula', '⊞ Cuadrícula'], ['list', '≡ Lista'], ['diagram', '⤢ Diagrama']].map(([v, lbl]) => (
              <button key={v} onClick={() => setView(v)}
                style={{ padding: '0.4rem 0.85rem', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: view === v ? 700 : 400, background: view === v ? 'var(--accent)' : 'var(--bg-input)', color: view === v ? '#fff' : 'var(--text-secondary)' }}>
                {lbl}
              </button>
            ))}
          </div>
          <button
            onClick={() => setDesignMode(m => !m)}
            title={designMode ? 'Mostrar selectores y filtros' : 'Ocultar selectores para maximizar área de trabajo'}
            style={{
              padding: '0.4rem 0.85rem', borderRadius: 7, fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
              border: designMode ? '1.5px solid var(--wall-accent)' : '1px solid var(--slate-300)',
              background: designMode ? 'rgba(139,92,246,.18)' : 'var(--bg-input)',
              color: designMode ? 'var(--wall-banner-bd)' : 'var(--text-secondary)',
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
            {designMode ? '🖌️ Modo diseño' : '⚙️ Selectores'}
          </button>
          <button onClick={load} disabled={loading}
            style={{ padding: '0.45rem 0.9rem', borderRadius: 7, border: '1px solid var(--slate-300)', background: 'var(--bg-input)', cursor: loading ? 'wait' : 'pointer', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
            {loading ? '⌛' : '↺'} Recargar
          </button>
        </div>
      </div>

      {/* ── Type selectors ── */}
      {!designMode && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', margin: '8px 14px 0', flexShrink: 0, background: 'var(--bg-card)', border: '1px solid var(--slate-300)', borderRadius: 10, padding: '0.7rem 1rem' }}>
          <TypeSelector side="left"  selected={leftTypes}  onChange={t => { setLeftTypes(t); setSelectedPort(null); setLeftFilterIds([]); }} />
          <TypeSelector side="right" selected={rightTypes} onChange={t => { setRightTypes(t); setSelectedPort(null); setRightFilterIds([]); }} />
        </div>
      )}

      {!designMode && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.6rem', margin: '8px 14px 0', flexShrink: 0, background: 'var(--bg-card)', border: '1px solid var(--slate-300)', borderRadius: 10, padding: '0.7rem 1rem' }}>
          <DeviceFilterControl label="Origen" options={leftFilterOptions} selectedIds={leftFilterIds} onChange={setLeftFilterIds} />
          <DeviceFilterControl label="Destino" options={rightFilterOptions} selectedIds={rightFilterIds} onChange={setRightFilterIds} />
        </div>
      )}

      {!designMode && view === 'cuadricula' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.6rem', margin: '8px 14px 0', flexShrink: 0, background: 'var(--bg-card)', border: '1px solid var(--slate-300)', borderRadius: 10, padding: '0.55rem 1rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, rowGap: 4, alignItems: 'center' }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700 }}>◀ Filas:</span>
            {[1, 2, 3, 4].map(n => (
              <button key={n} onClick={() => setLeftRows(n)}
                style={{
                  padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: leftRows === n ? 700 : 400, cursor: 'pointer',
                  border: leftRows === n ? '1.5px solid var(--accent)' : '1px solid var(--slate-300)',
                  background: leftRows === n ? 'rgba(var(--accent-rgb),.18)' : 'var(--bg-input)',
                  color: leftRows === n ? 'var(--accent-l)' : 'var(--text-secondary)',
                }}>
                {n === 1 ? '1 fila' : `${n} filas`}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, rowGap: 4, alignItems: 'center' }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700 }}>Filas ▶:</span>
            {[1, 2, 3, 4].map(n => (
              <button key={n} onClick={() => setRightRows(n)}
                style={{
                  padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: rightRows === n ? 700 : 400, cursor: 'pointer',
                  border: rightRows === n ? '1.5px solid var(--accent)' : '1px solid var(--slate-300)',
                  background: rightRows === n ? 'rgba(var(--accent-rgb),.18)' : 'var(--bg-input)',
                  color: rightRows === n ? 'var(--accent-l)' : 'var(--text-secondary)',
                }}>
                {n === 1 ? '1 fila' : `${n} filas`}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div style={{ color: 'var(--error-text)', padding: '0.75rem 1rem', background: 'var(--error-bg)', borderRadius: 8, margin: '10px 14px 0', flexShrink: 0, fontSize: '0.85rem' }}>{error}</div>
      )}

      {/* ── Pending source banner ── */}
      {selectedPort && (
        <div style={{ background: 'rgba(var(--accent-rgb),.1)', border: '2px solid var(--accent)', borderRadius: 10, padding: '0.6rem 1rem', margin: '10px 14px 0', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.85rem', flexWrap: 'wrap' }}>
          <span>{DEVICE_TYPES[selectedPort.typeKey]?.icon}</span>
          <span style={{ flex: 1 }}>
            Conectando desde <strong>{selectedPort.deviceName}</strong>{' · '}
            <strong>{selectedPort.label || `P${selectedPort.port_number}`}</strong>
            {selectedPort.speed ? ` (${selectedPort.speed})` : ''}
            {' '}— selecciona un puerto libre en la columna derecha
          </span>
          <button onClick={() => setSelectedPort(null)}
            style={{ padding: '0.25rem 0.7rem', borderRadius: 5, border: '1px solid var(--accent-l)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--accent)', fontWeight: 600 }}>
            ✕ Cancelar (Esc)
          </button>
        </div>
      )}

      {/* ── Legend ── */}
      {!designMode && (
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', margin: '10px 14px 0', flexShrink: 0, fontSize: '0.72rem', color: 'var(--text-muted)' }}>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Leyenda:</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, border: '2px solid var(--border)', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Libre</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, border: '2px solid var(--accent)', background: 'rgba(var(--accent-rgb),.1)', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Seleccionado</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, border: '2px solid var(--conn-banner-bd)', background: 'var(--conn-banner-bg)', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Disponible para conectar</span>
          <span>Dot de color = cable conectado · Hover = detalles · Clic = conectar / ✂️ cortar</span>
        </div>
      )}

      {/* ── Main content ── */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '10px 14px 12px', overflow: 'hidden' }}>
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'var(--text-muted)', fontSize: '0.9rem', gap: 10 }}>
          <div style={{ width: 20, height: 20, border: '2px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          Cargando dispositivos y puertos…
        </div>
      ) : view === 'cuadricula' ? (
        <CuadriculaView
          leftGroups={leftFiltered}
          rightGroups={rightFiltered}
          selectedPort={selectedPort}
          onLeftPortClick={handleLeftPortClick}
          onRightPortClick={handleRightPortClick}
          onDisconnect={c => setDisconnecting(c)}
          leftRows={leftRows}
          rightRows={rightRows}
        />
      ) : view === 'list' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'minmax(0, 1fr)', gap: '2rem', alignItems: 'stretch', flex: 1, minHeight: 0 }}>
          <ListaColumn groups={leftFiltered} side="left" selectedPort={selectedPort}
            onPortClick={handleLeftPortClick}
            onDisconnect={c => setDisconnecting(c)} />
          <ListaColumn groups={rightFiltered} side="right" selectedPort={selectedPort}
            onPortClick={handleRightPortClick}
            onDisconnect={c => setDisconnecting(c)}
            sourceDeviceId={selectedPort?.device?.id} />
        </div>
      ) : (
        <DiagramView
          leftGroups={leftFiltered}
          rightGroups={rightFiltered}
          onDisconnect={c => setDisconnecting(c)}
        />
      )}
      </div>

      {/* ── ConnectForm ── */}
      {connecting && selectedPort && (
        <ConnectForm
          sourcePort={selectedPort}
          sourceMeta={{ typeKey: selectedPort.typeKey, portType: selectedPort.portType || DEVICE_TYPES[selectedPort.typeKey]?.portType, deviceName: selectedPort.deviceName }}
          destPort={connecting.destPort}
          destMeta={connecting.destMeta}
          companyId={companyId}
          onSave={handleConnectSaved}
          onCancel={() => setConnecting(null)}
        />
      )}

      {/* ── ConnectionActionModal ── */}
      {disconnecting && (
        <ConnectionActionModal
          conn={disconnecting}
          companyId={companyId}
          onDone={handleDisconnectDone}
          onCancel={() => setDisconnecting(null)}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
